import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import get from 'lodash/get';
import take from 'lodash/take';
import orderBy from 'lodash/orderBy';
import takeRight from 'lodash/takeRight';
import cloneDeep from 'lodash/cloneDeep';
import uniqueId from 'lodash/uniqueId';
import isEmpty from 'lodash/isEmpty';
import { DialogType } from 'office-ui-fabric-react/lib/Dialog';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { PrimaryButton } from 'office-ui-fabric-react';
import { getUser } from '../../utils/auth';
import SubModuleHeader from '../../components/SubModuleHeader';
import SubModule from '../../components/SubModule';
import Module from '../../components/Module';
import CardHeader from '../../components/CardHeader';
import ModuleHeader from '../../components/ModuleHeader/ModuleHeader';
import ModuleContent from '../../components/ModuleContent';
import SubModuleContent from '../../components/SubModuleContent';
import StackGrid from '../../components/StackGrid';
import DataItem from '../../components/DataItem';
import PageTitle from '../../components/PageTitle';
import Main from '../../components/Main';
import { fetchBundle, fetchBundleActivity, deleteBundle } from '../../actions/bundles';
import { showFlashMessage } from '../../actions/ui';
import BundleVersionList from '../../components/BundleVersionList';
import BundleActivity from '../../components/BundleActivity';
import TextButton from '../../components/TextButton';
import { formatDateString } from '../../utils';
import { deleteBundleContent as dialogContent } from '../../constants';
import ReviseBundleDialog from '../../components/ReviseBundleDialog';
import styles from './BundleDetails.style';

const createdBundleLabels = {
  editedBy: 'Created By',
  date: 'Date Created',
  isRevised: false,
};

const revisedBundleLabels = {
  editedBy: 'Revised By',
  date: 'Date Revised',
  isRevised: true,
};

const BundleDetails = () => {
  const dispatch = useDispatch();
  const { bundleId } = useParams();
  const { pathname, state: locationState } = useLocation();
  const history = useHistory();
  const user = getUser();
  const userName = get(user, 'profile.name', '');

  const [bundle, setBundle] = useState({});
  const [count, setCount] = useState(3);
  const [isReviseBundle, setIsReviseBundle] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bundleVersionToRevise, setBundleVersionToRevise] = useState([]);
  const [bundleDefinitions, setBundleDefinitions] = useState([]);
  const [bundleLabels, setBundleLabels] = useState(createdBundleLabels);
  const [isSaveEnable, setIsSaveEnable] = useState(true);
  const [isDialogVisible, setIsDialogVisible] = useState(false);

  const ref = useRef();

  const bundleVersions = useSelector(state =>
    orderBy(
      Object.values(get(state, `entities.bundle`, [])).filter(data => {
        return data.bundleId === bundleId;
      }),
      ['createdTimeStamp'],
    ),
  );

  const activities = useSelector(state =>
    orderBy(
      Object.values(get(state, `entities.activity`, {})).filter(data => {
        return data.newData?.bundleId === bundleId;
      }),
      ['timestamp'],
      ['desc'],
    ),
  );

  useEffect(() => {
    if (!isReviseBundle) {
      dispatch(fetchBundle(bundleId));
      setTimeout(() => {
        dispatch(fetchBundleActivity(bundleId));
      }, 1500);
    }
  }, [dispatch, bundleId, isReviseBundle]);

  useEffect(() => {
    if (Array.isArray(bundleVersions) && bundleVersions.length) {
      setBundle(bundleVersions.slice(-1).pop());
    }
  }, [bundleVersions]);

  useEffect(() => {
    if (locationState) {
      setIsDialogVisible(locationState.delete);
    } else {
      setIsDialogVisible(false);
    }
  }, [locationState]);

  useEffect(() => {
    if (pathname) {
      setIsReviseBundle(pathname.includes('edit'));
      setIsDialogVisible(false);
    }
  }, [pathname]);

  useEffect(() => {
    if (!isEmpty(bundle)) {
      const bundleData = cloneDeep(bundleVersions);
      bundleData.map((item, index) => {
        const tempBundle = item;
        if (index === 0) {
          tempBundle.version = 'Original Bundle Definition';
        } else {
          tempBundle.version = `Bundle Revision ${index}`;
        }
        return tempBundle;
      });
      setBundleDefinitions(bundleData);
      if (isReviseBundle) {
        const bundleToRevise = cloneDeep(takeRight(bundleData));
        bundleToRevise.map(item => {
          const tempBundle = item;
          tempBundle._id = uniqueId();
          tempBundle.version = `Bundle Revision ${bundleData.length}`;
          tempBundle.createdTimeStamp = new Date().toDateString();
          tempBundle.createdBy = userName;
          return tempBundle;
        });
        setBundleVersionToRevise(bundleToRevise);
      }
      if (bundleVersions.length > 1 || bundle.locked) {
        setBundleLabels(revisedBundleLabels);
      }
    }
  }, [bundle, isReviseBundle]);

  const handleVersionChange = isEnable => {
    setIsSaveEnable(isEnable);
  };

  const handleCancel = () => {
    setIsSaveEnable(true);
    history.replace(`/bundles/${bundleId}`);
  };

  const handleSuccess = bundleRevised => {
    history.push(`/bundles/${bundleRevised ? bundleRevised.bundleId : bundleId}`);
  };

  const handleSave = async () => {
    setIsSubmitting(true);

    await ref.current.submitForm();
    setIsSubmitting(false);
  };

  const handleDismiss = () => {
    history.replace({ state: null });
  };

  const handleDialogSubmit = async () => {
    setIsSubmitting(true);
    const result = await dispatch(deleteBundle(bundleId));
    setIsSubmitting(false);
    if (result && !result.error) {
      history.push({ pathname: '/products', state: { tabName: 'bundles' } });
      dispatch(showFlashMessage('Product bundle successfully deleted', { type: 'success' }));
    }
  };

  return (
    <>
      <PageTitle>{bundle.bundleName}</PageTitle>
      <Main>
        <Module>
          <ModuleContent>
            <CardHeader variant="large" leftBorder>
              {`Bundle ID: #${bundle.bundleId || ''}`}
            </CardHeader>
            <SubModule>
              <SubModuleContent>
                <StackGrid
                  smCols={1}
                  mdCols={2}
                  lgCols={2}
                  gap={8}
                  parentClassName={styles.meta}
                  childClassName={styles.metaChild}
                >
                  {bundle.bundleDescription && (
                    <div>
                      <>
                        <Text block variant="medium">
                          <strong>Description</strong>
                        </Text>
                        <Text block variant="small" className={styles.description}>
                          {bundle.bundleDescription}
                        </Text>
                      </>
                    </div>
                  )}
                  <div>
                    {bundle.bundleType && (
                      <DataItem label="Bundle Type">{bundle.bundleType}</DataItem>
                    )}
                    {bundle.createdBy && (
                      <DataItem label={bundleLabels.editedBy}>{bundle.createdBy}</DataItem>
                    )}
                    {bundle.createdTimeStamp && (
                      <DataItem label={bundleLabels.date}>
                        {bundleLabels.isRevised && bundle.lastUpdatedTimestamp
                          ? formatDateString(bundle.lastUpdatedTimestamp)
                          : formatDateString(bundle.createdTimeStamp)}
                      </DataItem>
                    )}
                    {bundleLabels.isRevised && (
                      <DataItem label="Revision Number">{bundleDefinitions?.length}</DataItem>
                    )}
                  </div>
                </StackGrid>
              </SubModuleContent>
            </SubModule>
            <SubModule disableAccordian>
              <SubModuleHeader id="bundleDefinitionHeading">Bundle Definitions</SubModuleHeader>
              <SubModuleContent data-testid="bundleDefinitionContent">
                <BundleVersionList
                  bundleVersions={bundleDefinitions}
                  noBorderBottom
                  isEditMode={isReviseBundle}
                />
              </SubModuleContent>
            </SubModule>
            {isReviseBundle && (
              <SubModule disableAccordian>
                <SubModuleHeader id="bundleDefinitionHeading">
                  Bundle Revision Range
                </SubModuleHeader>
                <SubModuleContent>
                  <BundleVersionList
                    bundleVersions={bundleVersionToRevise}
                    ref={ref}
                    onUpdate={handleVersionChange}
                    onSuccess={handleSuccess}
                    noBorderBottom
                    edit
                  />
                </SubModuleContent>
              </SubModule>
            )}
          </ModuleContent>
        </Module>
        {!isReviseBundle && (
          <Module>
            {activities && activities.length > 0 && (
              <ModuleHeader
                extra={
                  <Text
                    aria-label="Expanded"
                    role="alert"
                    as="span"
                    className={styles.activityShowing}
                  >
                    Showing{' '}
                    {activities.length > (activities.length > count ? count - 3 : count) ? (
                      <span>{count}</span>
                    ) : (
                      <span>{activities.length}</span>
                    )}{' '}
                    of <span>{activities.length}</span>
                  </Text>
                }
                className={styles.bundleActivity}
              >
                Bundle Activity
              </ModuleHeader>
            )}
            <ModuleContent className={styles.activityContent}>
              {activities && <BundleActivity items={take(activities, count)} />}
              {activities && activities.length > 3 && activities.length > count && (
                <div>
                  <TextButton
                    tabIndex="0"
                    noPadding
                    aria-label="Show More Activity"
                    className={styles.showMoreActivityButton}
                    onClick={() => setCount(count + 10)}
                  >
                    <Text as="p" variant="small" className={styles.showMoreActivity}>
                      Show More Activity
                    </Text>
                  </TextButton>
                </div>
              )}
            </ModuleContent>
          </Module>
        )}
        {isReviseBundle && (
          <div className={styles.buttonContainer}>
            <TextButton
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </TextButton>
            <PrimaryButton
              type="submit"
              onClick={handleSave}
              disabled={isSubmitting || !isSaveEnable}
            >
              Save
            </PrimaryButton>
          </div>
        )}
      </Main>
      <ReviseBundleDialog
        onDismiss={handleDismiss}
        onSubmit={handleDialogSubmit}
        isRemoving={isSubmitting}
        hidden={!isDialogVisible}
        dialogContentProps={{
          type: DialogType.close,
          title: dialogContent.title,
          closeButtonAriaLabel: 'Close',
        }}
        dialogBodyContent={{
          content: `${dialogContent.content} ${bundle?.bundleName}?`,
          buttonText: dialogContent.button,
          showCancelButton: dialogContent.showCancelButton,
        }}
      />
    </>
  );
};

export default BundleDetails;
